import { Link } from 'react-router-dom';

export default function FocusWritingClass() {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const classStructure = [
    {
      step: 1,
      icon: 'ri-book-line',
      title: 'Content Knowledge & General Knowledge',
      duration: '40 minutes',
      description: 'Building foundational knowledge and vocabulary through engaging topics and discussions',
      color: '#153223'
    },
    {
      step: 2,
      icon: 'ri-cup-line',
      title: 'Break Time',
      duration: '5 minutes',
      description: 'Short refreshing break to recharge for focused writing practice',
      color: '#FFE66D',
      isBreak: true
    },
    {
      step: 3,
      icon: 'ri-edit-line',
      title: 'Essay Writing Dictation / Vocabulary Drills',
      duration: '45 minutes',
      description: 'Intensive listening and writing practice to develop focus, accuracy, and writing fluency',
      color: '#2d5a3f'
    },
    {
      step: 4,
      icon: 'ri-home-line',
      title: 'Homework Writing Practice',
      duration: 'At Home',
      description: 'Weekly writing assignments based on class focus to reinforce learning and build consistency',
      color: '#3d6a4f'
    }
  ];

  const writingSchedule = [
    { month: 'January', focus: 'Letter Writing', assessment: '' },
    { month: 'February', focus: 'Descriptive Writing', assessment: 'Writing Test' },
    { month: 'March', focus: 'Narrative Writing', assessment: '' },
    { month: 'April', focus: 'Narrative Writing', assessment: 'Writing Test' },
    { month: 'May', focus: 'Article Writing', assessment: '' },
    { month: 'June', focus: 'Speech and Talk Writing', assessment: 'Writing Test' },
    { month: 'July', focus: 'Email and Recount Writing', assessment: '' },
    { month: 'August', focus: 'Report Writing', assessment: 'Writing Test' },
    { month: 'September', focus: 'Review Writing', assessment: '' },
    { month: 'October', focus: 'Revision of Key Formats', assessment: 'Writing Test' },
    { month: 'November', focus: 'Free Choice Writing', assessment: 'Year-End Writing Competition' },
    { month: 'December', focus: 'Portfolio Month', assessment: 'Portfolio Review & Reflection' }
  ];

  const keyFeatures = [
    {
      icon: 'ri-focus-3-line',
      title: 'Structured Learning',
      description: 'Every class follows a proven format that builds both content knowledge and writing skills systematically'
    },
    {
      icon: 'ri-chat-voice-line',
      title: 'Dictation Training',
      description: 'Essay writing dictation develops listening skills, focus, and accuracy in written expression'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Regular Assessment',
      description: 'Monthly tests and competitions track progress and motivate continuous improvement'
    },
    {
      icon: 'ri-book-2-line',
      title: 'Diverse Writing Styles',
      description: 'Students master 8+ different writing formats from letters to reviews throughout the year, aligned with IGCSE writing syllables. '
    }
  ];

  return (
    <main className="bg-[#F5F1E8] min-h-screen">
      {/* Header */}
      <section className="bg-[#153223] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link 
            to="/"
            onClick={handleScrollToTop}
            className="inline-flex items-center gap-2 text-[#FFE66D] hover:text-white transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <h1 className="font-['Yeseva_One',serif] text-4xl md:text-5xl mb-4">
            Focus Writing Class
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl">
            A comprehensive writing program that combines content knowledge with structured writing practice, aligned with IGCSE-style writing expectations, for students aged 11–15.
          </p>
        </div>
      </section>

      {/* Age and Duration Info */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#FFE66D] rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <i className="ri-user-line text-3xl text-[#153223]"></i>
                <h2 className="font-['Yeseva_One',serif] text-2xl text-[#153223]">
                  Age Group
                </h2>
              </div>
              <p className="text-xl text-[#153223] font-semibold">
                Ages 11–15
              </p>
            </div>
            <div className="bg-[#153223] rounded-2xl p-6 text-center text-white">
              <div className="flex items-center justify-center gap-3 mb-3">
                <i className="ri-time-line text-3xl text-[#FFE66D]"></i>
                <h2 className="font-['Yeseva_One',serif] text-2xl">
                  Class Duration
                </h2>
              </div>
              <p className="text-xl text-gray-200">
                90 minutes per session
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Class Structure */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-4">
            How Each Class Works
          </h2>
          <p className="text-center text-gray-700 text-lg mb-12 max-w-2xl mx-auto">
            A structured routine that ensures students improve consistently in both content knowledge and writing skills
          </p>

          <div className="space-y-6">
            {classStructure.map((item, index) => (
              <div key={index} className="relative">
                <div 
                  className={`${item.isBreak ? 'bg-[#FFF9CC]' : 'bg-white'} rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300`}
                >
                  <div className="flex items-start gap-6">
                    <div 
                      className="w-20 h-20 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ 
                        backgroundColor: item.isBreak ? '#FFE66D' : item.color,
                        color: item.isBreak ? '#153223' : 'white'
                      }}
                    >
                      <i className={`${item.icon} text-3xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {!item.isBreak && (
                          <span className="bg-[#FFE66D] text-[#153223] font-bold px-3 py-1 rounded-full text-sm">
                            Step {item.step}
                          </span>
                        )}
                        <span className="text-gray-600 text-sm font-semibold">
                          {item.duration}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#153223] mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Arrow connector */}
                {index < classStructure.length - 1 && (
                  <div className="flex justify-center py-4">
                    <i className="ri-arrow-down-line text-4xl text-[#153223]"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Writing Schedule */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-4">
            A Sample Version of Our Year-Long Writing Curriculum
          </h2>
          <p className="text-center text-gray-700 text-lg mb-12 max-w-3xl mx-auto">
            A carefully planned progression through different writing styles with regular assessments and competitions
          </p>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
              <thead>
                <tr className="bg-[#153223] text-white">
                  <th className="px-6 py-4 text-left font-bold text-lg">Month</th>
                  <th className="px-6 py-4 text-left font-bold text-lg">Writing Focus</th>
                  <th className="px-6 py-4 text-left font-bold text-lg">Assessment</th>
                </tr>
              </thead>
              <tbody>
                {writingSchedule.map((item, index) => (
                  <tr 
                    key={index}
                    className={`${index % 2 === 0 ? 'bg-[#F5F1E8]' : 'bg-white'} hover:bg-[#FFE66D]/20 transition-colors`}
                  >
                    <td className="px-6 py-4 font-semibold text-[#153223]">
                      {item.month}
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {item.focus}
                    </td>
                    <td className="px-6 py-4">
                      {item.assessment ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          item.assessment.includes('Competition') 
                            ? 'bg-[#FFE66D] text-[#153223]' 
                            : item.assessment.includes('Portfolio')
                            ? 'bg-[#153223] text-white'
                            : 'bg-[#2d5a3f] text-white'
                        }`}>
                          {item.assessment}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">Practice Month</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] text-center mb-12">
            Why Focus Writing Class Works
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#153223] rounded-full mx-auto mb-4">
                  <i className={`${feature.icon} text-3xl text-[#FFE66D]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#153223] mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-['Yeseva_One',serif] text-4xl text-[#153223] mb-6">
                What Students Achieve
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFE66D] rounded-full flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-xl text-[#153223]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#153223] mb-2">Enhanced Writing Fluency</h3>
                    <p className="text-gray-700">Students develop speed and accuracy in writing through regular dictation practice</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFE66D] rounded-full flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-xl text-[#153223]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#153223] mb-2">Diverse Writing Skills</h3>
                    <p className="text-gray-700">Master multiple writing formats essential for academic and real-world communication</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFE66D] rounded-full flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-xl text-[#153223]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#153223] mb-2">Improved Focus & Listening</h3>
                    <p className="text-gray-700">Writing dictation builds concentration and active listening abilities</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFE66D] rounded-full flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-xl text-[#153223]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#153223] mb-2">IGCSE Preparation</h3>
                    <p className="text-gray-700">Strong foundation for advanced writing requirements in secondary education</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/6d3f387e5f6f9ffa4f6f2ec6de0e13a6.jpg"
                alt="Student focused on writing practice"
                className="w-full h-full object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-[#153223] text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-['Yeseva_One',serif] text-3xl md:text-4xl mb-4 md:mb-6">
            Ready to Develop Your Child's Writing Skills?
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-6 md:mb-8">
            Join our Focus Writing Class and watch your child's confidence in writing grow month by month
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-3 md:gap-4">
            <a
              href="https://wa.me/60198950116"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap bg-[#FFE66D] hover:bg-[#FFD93D] text-[#153223] font-bold px-6 py-3 md:px-8 md:py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center gap-2 text-base md:text-lg"
            >
              <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
              <span className="text-sm md:text-base">Enquire About Focus Writing Class</span>
            </a>
            <Link
              to="/"
              onClick={handleScrollToTop}
              className="whitespace-nowrap bg-white hover:bg-gray-100 text-[#153223] font-bold px-6 py-3 md:px-8 md:py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center gap-2 text-base md:text-lg"
            >
              <i className="ri-home-line text-xl md:text-2xl"></i>
              <span className="text-sm md:text-base">Back to Home</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}